import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, ListItemIcon, ListItemText, MenuItem, Toolbar } from "@mui/material";
import {
  ColumnSettingsPopover,
  ColumnSettingsPopoverProps
} from "components/reports/ReportMeasuresTable/ColumnSettingsPopover";
import { ColumnSettings } from "components/reports/ReportMeasuresTable/ReportMeasuresTable";
import { benchmarkColumnsFreemium, benchmarkFilters } from "constants/benchmark";
import { useDevice } from "hooks";
import {
  MaterialReactTable,
  MaterialReactTableProps,
  type MRT_ColumnDef,
  useMaterialReactTable
} from "material-react-table";
import { useGetBenchmarkFilters, useGetBenchmarkTable } from "queries/query-selectors/benchmark-query-selectors";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector } from "store";
import { ClientDealTableRow, CurrencyType, DealTableColumn, ReportFilters, ReportTableColumn } from "store/types";

import { ParametersPicker } from "../../Benchmark/Table-components/ParametersPicker";
import { StaticFilters } from "../../Benchmark/Table-components/StaticFilters";
import { TableHeader } from "../../Benchmark/Table-components/TableHeader";
import { TableCell } from "../../reports/Measures-table/TableCell";

interface Props {
  currency: CurrencyType;
}

const defaultFilters = { [benchmarkFilters.SUB_ASSET]: [], [benchmarkFilters.VINTAGE]: [] };

const mapFilterKeyToServer = (key: string): string => {
  const keyMapping: Record<string, string> = {
    [benchmarkFilters.SUB_ASSET]: "Sub Asset Class",
    [benchmarkFilters.VINTAGE]: "Vintage"
  };
  return keyMapping[key] || key;
};

export const BenchmarkTable: React.FC<Props> = ({ currency }) => {
  const { benchmarkTableColumns, benchmarkIndexName } = useReportsQueriesSelector();
  const { data: filterValues } = useGetBenchmarkFilters({});
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const { isMobile } = useDevice();
  const isFreemiumMode = useAccountSelector()?.is_freemium || false;

  const { data, isLoading, isFetching, error } = useGetBenchmarkTable({
    benchmarkTableColumns,
    currency,
    groupBy: [[benchmarkFilters.SUB_ASSET], [benchmarkFilters.VINTAGE]],
    exchangeMethod: "toDate",
    benchmarkIndexName: benchmarkIndexName,
    ...Object.entries(filters).reduce(
      (acc, [key, values]) => {
        if (values.length) {
          return { filters: { ...acc.filters, [mapFilterKeyToServer(key)]: values } };
        }
        return acc;
      },
      { filters: {} }
    )
  });

  const { setQueriesAction } = useReportsActions();

  //if client faced with error (like 402 - payment required), just set default freemium columns and request table data with allowed columns
  useEffect(() => {
    if (error && isFreemiumMode) {
      setQueriesAction({
        benchmarkTableColumns: benchmarkColumnsFreemium
      });
    }
  }, [error, isFreemiumMode]);

  const columnRefs = useRef(new Map());

  const getColumnRef = (colId: string) => {
    if (!columnRefs.current.has(colId)) {
      columnRefs.current.set(colId, React.createRef<HTMLElement>());
    }
    return columnRefs.current.get(colId);
  };

  const [columnSettings, setColumnSettings] = useState<ColumnSettings>({ open: false, isDuplicate: false });

  const handleColumnSettings: ColumnSettingsPopoverProps["onApply"] = (queries) => {
    if (columnSettings.column) {
      const { column, isDuplicate } = columnSettings;
      const newKey = isDuplicate ? column.key + "-dup" : column.key;
      const newColumn: ReportTableColumn = {
        ...column,
        dateRange: queries?.dateRange,
        currency: queries?.currency,
        key: newKey
      };
      const index = benchmarkTableColumns.findIndex((col) => col === column);

      setQueriesAction({
        benchmarkTableColumns: [
          ...benchmarkTableColumns.slice(0, isDuplicate ? index + 1 : index),
          newColumn,
          ...benchmarkTableColumns.slice(index + 1)
        ]
      });
    }
    setColumnSettings({ open: false, isDuplicate: false });
  };

  const handleDelete = (column: ReportTableColumn) => {
    const index = benchmarkTableColumns.findIndex((col) => col.key === column?.key);
    if (index > -1) {
      setQueriesAction({
        benchmarkTableColumns: [...benchmarkTableColumns.slice(0, index), ...benchmarkTableColumns.slice(index + 1)]
      });
    }
  };

  const tableProps: Partial<MaterialReactTableProps<ClientDealTableRow>> = {
    enablePagination: false,
    enableDensityToggle: false,
    muiTableContainerProps: {
      sx: {
        padding: "0px",
        maxHeight: "400px",
        overflow: "auto",
        borderRadius: "8px",
        border: "1px solid #DBE8F5",
        paddingBottom: "4px",
        "& .MuiTableBody-root": {
          "& .MuiTableRow-root:last-child": {
            "& .MuiTableCell-root": {
              paddingBottom: "16px"
            }
          }
        }
      }
    },
    muiTableProps: {},
    muiTablePaperProps: {
      sx: {
        height: "100%",
        bgcolor: "white",
        boxShadow: 0,
        "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
          "&:first-child": {
            overflow: "visible !important"
          }
        }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        "&:first-child": {
          borderTop: "none"
        },
        "&:first-of-type": {
          borderRight: "none",
          borderTop: "none",
          borderBottom: "0.5px solid #DBE8F5",
          borderLeft: "0.5px solid #DBE8F5"
        },
        "&:not(:first-of-type):not(:nth-of-type(2))": {
          borderLeft: "0.5px solid #DBE8F5",
          borderTop: "none",
          borderBottom: "0.5px solid #DBE8F5"
        },
        "&:nth-of-type(2)": {
          borderTop: "none",
          borderBottom: "0.5px solid #DBE8F5"
        }
      }
    },
    muiTableHeadCellProps: {
      sx: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }
    },
    muiTableHeadRowProps: {
      sx: {
        // backgroundColor: "#F6F8FC",
        opacity: 1,
        maxHeight: "64px",
        borderRadius: "11px",
        boxShadow: "none"
      }
    },
    muiTableBodyRowProps: { sx: { backgroundColor: "white" } },
    defaultColumn: {
      muiColumnActionsButtonProps: { sx: { opacity: 0 } },
      muiTableHeadCellProps: {
        sx: {
          " .Mui-TableHeadCell-Content": { flexGrow: 1 },
          " .MuiDivider-root": {
            borderWidth: 1,
            opacity: 0
          }
        }
      }
    },
    icons: {
      ArrowDownwardIcon: (props: { className: string }) => (
        <div className="measures-table__sort-icons">
          <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
          <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
        </div>
      ),
      DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
    },
    muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
    muiExpandAllButtonProps: { color: "primary" },
    muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } }
  };

  const columnsBenchmark = useMemo<MRT_ColumnDef<ClientDealTableRow>[]>(() => {
    if (!data) return [];

    const {
      tableData: { columns }
    } = data;

    const columnsTable: MRT_ColumnDef<ClientDealTableRow>[] = benchmarkTableColumns.map<
      MRT_ColumnDef<ClientDealTableRow>
    >((col, index) => {
      const colId = col.key + "-" + index;
      const columnData: DealTableColumn = columns.find((column) => column.key === col.key) || {
        key: "",
        title: "",
        postfix: ""
      };
      return {
        header: "",
        Header: ({ column }) => (
          <TableHeader
            indexName={benchmarkIndexName?.name}
            isBenchmarkTable={true}
            column={column}
            onClick={(e) => {
              e.stopPropagation();
              setColumnSettings({
                open: true,
                column: col,
                isDuplicate: false,
                anchorEl: getColumnRef(colId).current
              });
            }}
          />
        ),
        id: colId,
        meta: { data: columnData, def: col },
        accessorKey: col.key,
        sortingFn: "basic",
        accessorFn: (row) => row.valueData[col.key!],
        muiTableHeadCellProps: { ...tableProps.defaultColumn?.muiTableHeadCellProps, ref: getColumnRef(colId) },
        Cell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[column.columnDef.accessorKey as string] || ""}
            postfix={
              row.original.postfixes?.[column.columnDef.accessorKey as string] ||
              columnData.postfix ||
              row.original.postfix
            }
          />
        ),
        AggregatedCell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[column.columnDef.accessorKey as string] || ""}
            postfix={
              row.original.postfixes?.[column.columnDef.accessorKey as string] ||
              columnData.postfix ||
              row.original.postfix
            }
            aggregated
          />
        )
      };
    });

    columnsTable.unshift({
      header: "Asset Class",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableSorting: false,
      enableColumnOrdering: false,
      muiTableHeadCellProps: {
        sx: {
          fontSize: "16px",
          color: "#0A3142",
          fontWeight: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "16px !important"
        }
      },
      Cell: ({ cell, row }) => (
        <TableCell
          indentation={row.getParentRow()?.original.deal_id ? 2 : 0}
          dealId={row.original.deal_id}
          value={cell.getValue() as string}
          formatDate
        />
      ),
      AggregatedCell: ({ cell, row }) => (
        <TableCell dealId={row.original.deal_id} value={cell.getValue() as string} aggregated formatDate />
      )
    });

    return columnsTable;
  }, [data, benchmarkIndexName, benchmarkTableColumns]);

  const table = useMaterialReactTable({
    ...tableProps,
    columns: columnsBenchmark,
    data: data?.tableData.rows || [],
    state: {
      isLoading: isLoading || isFetching,
      columnOrder: columnOrder
    },
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableStickyHeader: true,
    enableExpanding: true,
    enableColumnDragging: true,
    enableColumnActions: true,
    enableColumnOrdering: true,
    onColumnOrderChange: setColumnOrder,
    enableBottomToolbar: false,
    layoutMode: "grid",
    enableColumnResizing: false,
    defaultColumn: {
      muiColumnDragHandleProps: {
        sx: { position: "absolute", left: -22, top: 0, opacity: 0.7, "&:hover": { opacity: 1 } }
      },
      enableColumnActions: true,
      renderColumnActionsMenuItems: ({ column, closeMenu, table }) => {
        const otherColumns = table.getAllColumns().filter((col) => col !== column);
        const deletable = otherColumns.some(
          //@ts-ignore
          (col) => col.columnDef.meta?.def?.aggregation === column.columnDef.meta?.def?.aggregation
        );
        const columnDef = benchmarkTableColumns.find((col) => col.key === column.columnDef.accessorKey);

        return [
          <MenuItem
            key="duplicate"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: columnDef!,
                isDuplicate: true,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Duplicate column</ListItemText>
          </MenuItem>,
          <MenuItem
            key="settings"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: columnDef!,
                isDuplicate: false,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <TuneIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Column settings</ListItemText>
          </MenuItem>,
          ...(deletable
            ? [
                <MenuItem
                  key="delete"
                  onClick={() => {
                    handleDelete(columnDef!);
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              ]
            : [])
        ];
      }
    },

    icons: {
      ArrowDownwardIcon: (props: { className: string }) => (
        <div className="measures-table__sort-icons">
          <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
          <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
        </div>
      ),
      DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
    },

    muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),

    muiExpandAllButtonProps: { color: "primary" },

    renderTopToolbar: () => (
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "10px" : 0,
          color: "#173c4c",
          fontSize: "16px",
          height: isMobile ? "auto" : "24px",
          minHeight: isMobile ? "auto" : "24px !important",
          maxHeight: isMobile ? "none" : "24px",
          px: "0 !important",
          mb: "16px"
        }}
      >
        <StaticFilters filters={filters} filterValues={filterValues || {}} setFilters={setFilters} />
        <ParametersPicker />
      </Toolbar>
    )
  });

  const popover = (
    <ColumnSettingsPopover
      {...columnSettings}
      onClose={() => setColumnSettings({ open: false, isDuplicate: false })}
      onApply={handleColumnSettings}
      isBenchmarkTable={true}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        // maxHeight: "400px",
        bgcolor: "transparent",
        boxShadow: "none",
        p: 0,
        borderRadius: 0,
        ".MuiPaper-root": {
          bgcolor: "transparent",
          boxShadow: "none",
          "> .MuiBox-root": {
            display: "none"
          }
        }
      }}
    >
      <MaterialReactTable table={table} />
      {popover}
    </Box>
  );
};
